import React, { useEffect, useRef } from 'react';
import { COLLAGE } from '../data/pexels';

/**
 * Wolverine-style scattered photo collage. Photos are absolutely positioned at
 * varied depths and drift at different speeds as the section scrolls through the
 * viewport, revealing a giant word behind them.
 */

// x/y in %, size in px, depth = parallax multiplier (bigger = faster).
const SPOTS = [
  { x: 4, y: 8, w: 190, depth: 0.9, r: -4 },
  { x: 78, y: 4, w: 150, depth: 1.4, r: 5 },
  { x: 30, y: 20, w: 130, depth: 0.5, r: 3 },
  { x: 60, y: 28, w: 210, depth: 1.1, r: -3 },
  { x: 10, y: 48, w: 160, depth: 1.6, r: 6 },
  { x: 84, y: 44, w: 175, depth: 0.7, r: -5 },
  { x: 44, y: 60, w: 150, depth: 1.2, r: 4 },
  { x: 68, y: 70, w: 195, depth: 0.6, r: -6 },
  { x: 18, y: 74, w: 165, depth: 1.0, r: 3 },
  { x: 50, y: 88, w: 135, depth: 1.5, r: -3 },
];

export const ParallaxCollage = (): React.JSX.Element => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const itemsRef = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    let raf = 0;
    const onScroll = (): void => {
      const sec = sectionRef.current;
      if (!sec) return;
      const rect = sec.getBoundingClientRect();
      const vh = window.innerHeight;
      // progress: -1 (below) → 0 (centered) → 1 (above)
      const progress = (vh / 2 - (rect.top + rect.height / 2)) / (vh / 2 + rect.height / 2);
      itemsRef.current.forEach((el, i) => {
        if (!el) return;
        const depth = SPOTS[i]?.depth ?? 1;
        const shift = -progress * depth * 90;
        // zoom: largest when the collage is centered, easing out toward the edges
        const scale = 1.02 + (1 - Math.min(1, Math.abs(progress))) * 0.09;
        el.style.transform = `translate3d(0, ${shift.toFixed(1)}px, 0) rotate(${SPOTS[i]?.r ?? 0}deg) scale(${scale.toFixed(3)})`;
      });
    };
    const loop = (): void => {
      onScroll();
      raf = requestAnimationFrame(loop);
    };
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) raf = requestAnimationFrame(loop);
    else onScroll();
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <section className="collage" ref={sectionRef} aria-hidden="true">
      <div className="collage-word">gather</div>
      <div className="collage-stage">
        {COLLAGE.map((p, i) => (
          <div
            key={p.id}
            className="collage-item"
            ref={(el) => {
              if (el) itemsRef.current[i] = el;
            }}
            style={{
              left: `${SPOTS[i]?.x ?? 0}%`,
              top: `${SPOTS[i]?.y ?? 0}%`,
              width: SPOTS[i]?.w ?? 160,
            }}
          >
            <img src={p.src} alt="" loading="lazy" />
          </div>
        ))}
      </div>
    </section>
  );
};
